import React, { useState, useEffect } from 'react'
import ReactModal from 'react-modal'
import { Box, Typography, CircularProgress, Paper, Divider } from '@mui/material'
import type { Assessment, QuestionResponse } from '../../types/assessments'
import { STATIC_LIKERT_OPTIONS } from '../../types/assessments'

interface AssessmentPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  assessment: any
}

// Helper to render icon - supports FontAwesome classes and emojis
const renderIcon = (icon: string) => {
  if (!icon) return '📝'
  // Check if it's a FontAwesome class (starts with 'fa-')
  if (icon.startsWith('fa-') || icon.startsWith('fas ') || icon.startsWith('far ')) {
    return <i className={`fas ${icon}`} style={{ fontSize: '24px' }}></i>
  }
  // Otherwise treat as emoji or text
  return icon
}

export const AssessmentPreviewModal: React.FC<AssessmentPreviewModalProps> = ({
  isOpen,
  onClose,
  assessment
}) => {
  const [questions, setQuestions] = useState<QuestionResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && assessment) {
      // Convert questions from string array to QuestionResponse objects
      const assessmentQuestions = assessment.questions || []
      const convertedQuestions: QuestionResponse[] = assessmentQuestions.map(
        (q: string, index: number) => ({
          id: index + 1,
          uuid: `temp-${index}`,
          assessmentId: assessment.uuid,
          order: index + 1,
          question: q,
          type: 'MCQ' as const,
          options: STATIC_LIKERT_OPTIONS,
          correctAnswers: [],
          status: 'ACTIVE' as const
        })
      )
      setQuestions(convertedQuestions)
    }
  }, [isOpen, assessment])

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '15px',
          border: 'none',
          boxShadow: '0 2px 8px 0 #00000029',
          padding: 0,
          maxWidth: '900px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }
      }}
      contentLabel='Assessment Preview'
    >
      <div className='card' style={{ margin: 0 }}>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <div>
            <h3 className='card-title fw-bolder mb-0'>{assessment.title}</h3>
            <p className='text-muted mb-0'>{assessment.subtitle}</p>
          </div>
          <button type='button' className='btn-close' onClick={onClose} aria-label='Close'></button>
        </div>

        <div
          className='card-body p-9'
          style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto' }}
        >
          {/* Assessment Info */}
          <div className='mb-10'>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: assessment.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  color: '#fff',
                  mr: 3
                }}
              >
                {renderIcon(assessment.icon)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  {assessment.description}
                </Typography>
              </Box>
            </Box>
          </div>

          <Divider sx={{ mb: 3 }} />

          {/* Questions Preview */}
          <div>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
              Questions ({questions.length})
            </Typography>

            {loading ? (
              <Box display='flex' justifyContent='center' py={4}>
                <CircularProgress />
              </Box>
            ) : questions.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color='text.secondary'>No questions available</Typography>
              </Paper>
            ) : (
              <Box>
                {questions.map((question, index) => (
                  <Paper
                    key={question.id || question.uuid}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: assessment.color }}>
                        Q{index + 1}
                      </Typography>
                      <span
                        className='badge'
                        style={{
                          backgroundColor: `${assessment.color}20`,
                          color: assessment.color,
                          fontSize: '0.7rem'
                        }}
                      >
                        {question.type}
                      </span>
                    </Box>

                    <Typography variant='body1' sx={{ fontWeight: 500, mb: 1.5 }}>
                      {question.question}
                    </Typography>

                    {question.options && question.options.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ mb: 1, display: 'block' }}
                        >
                          Options:
                        </Typography>
                        {question.options.map((option, optIndex) => (
                          <Box
                            key={optIndex}
                            sx={{
                              p: 1,
                              mb: 0.5,
                              borderRadius: 1,
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
                              {String.fromCharCode(65 + optIndex)}. {option}
                            </Typography>
                            {question.correctAnswers?.includes(optIndex) && (
                              <span className='badge bg-success' style={{ fontSize: '0.7rem' }}>
                                Correct
                              </span>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: '1px solid #e0e0e0'
                      }}
                    ></Box>
                  </Paper>
                ))}
              </Box>
            )}
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end'>
          <button type='button' className='btn btn-primary' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </ReactModal>
  )
}
